import { useConfig } from '@nexelem/config';
import { createDrawer, Drawer } from '@nexelem/drawer';
import { useAuthContext } from '../../../../../providers/AuthProvider';
import { PERMS } from '../../../../../routes/permissions';
import { Indicator, Button } from '@nexelem/components';
import { FilterData, FilterDataTyped } from '@nexelem/filters';
import { debounce, defaultsDeep } from 'lodash';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { openCancelTasksModal } from '../../../../../pages/generic/Products/components/TaskDetailDrawer/components/CancelTasksModal';
import { useSelectAllStore } from '../../../ToDoList/store/useSelectAllStore';
import { useSimulationId } from '../../GanttSimulation/hooks';
import { TasksDrawerEmptyState } from './components/TasksDrawerEmptyState';
import { DrawerFiltersData, TasksDrawerFilters } from './components/TasksDrawerFilters';
import { useTasksView } from './hooks/useTasksView';
import { TaskViewType } from './types';
import { useFiltersStore } from './hooks/useFiltersStore';
import { useCustomTranslation, useInvalidateQuery } from '../../../../../hooks';
import { GRAPHNE_TODO_LIST_KEY, useGraphneTodoList } from '../../../../../queryHooks/productionPlanHooks';
import { AvailableTaskTypes, ToDoListFlatView, ToDoListTreeView } from '../../../ToDoList';
import { openAutoPlanModal } from '../../../../../pages/generic/Products/components/TaskDetailDrawer/components/AutoPlanModal';
import { toggleTaskDetailsDrawer } from '../../../../../pages/generic/Products/components/TaskDetailDrawer/TaskDetailsDrawer';
import { TreeViewSkeleton } from './components/TreeViewSkeleton';
import { FlatViewSkeleton } from './components/FlatViewSkeleton';
import { TaskMainValueOptions, useProductionPlanSettings } from '../SettingsDrawer';

enum DISPLAY_STATE {
  LOADING = 'loading',
  EMPTY = 'empty',
  SHOW = 'show',
}

const defaultFilters: DrawerFiltersData = {
  taskName: '',
  dueDate: { from: '', to: '' },
  taskTypes: [],
  showOrders: false,
  queues: [],
};

export const ProductionPlanTasksDrawer = () => {
  const displayField = useProductionPlanSettings((state) => state.taskMainValue);
  const { data } = useGraphneTodoList();
  const { modules } = useConfig();
  const { id: simulationId } = useSimulationId();
  const { hasPermission } = useAuthContext();
  const canEditPlan = hasPermission(PERMS.PERM_4);
  const canUsePlanningAlgorithm = hasPermission(PERMS.PERM_5);

  const totalToDoTasks = data?.total || 0;
  const { tWrapper } = useCustomTranslation('products.productionPlan');

  const mainField = useMemo(
    () => (displayField === TaskMainValueOptions.PRODUCT ? 'productName' : 'name'),
    [displayField],
  );

  const [isPopperVisible, setIsPopperVisible] = useState(false);

  const { filters, isTreeViewEnabled, setFilters, setTreeView } = useFiltersStore();
  const { selectAll } = useSelectAllStore();

  const {
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    tasksList,
    taskViewType,
    checkTasksIds,
    totalTasks,
    handleTaskCheck,
    onCheckAll,
    isCheckAllFilterChecked,
    queryKey,
  } = useTasksView({
    isTreeViewEnabled,
    filters,
  });

  const invalidateList = useInvalidateQuery(queryKey);
  const invalidateTotal = useInvalidateQuery(GRAPHNE_TODO_LIST_KEY);
  const invalidateQueries = useCallback(async () => {
    onCheckAll(false);
    await Promise.all([invalidateList(), invalidateTotal()]);
  }, [invalidateList, invalidateTotal, onCheckAll]);

  const handleMoreClick = () => (e: SyntheticEvent, task: AvailableTaskTypes) => {
    e.preventDefault();
    e.stopPropagation();
    toggleTaskDetailsDrawer({
      taskId: task.id.toString(),
      withBackToTaskDrawerButton: true,
    });
  };

  const onAutoplanModalToggle = () => {
    const isAnyTaskChecked = checkTasksIds.length > 0 || selectAll;
    if (isAnyTaskChecked) {
      openAutoPlanModal({
        checkedTasksId: checkTasksIds,
        onSuccess: invalidateQueries,
        totalTasks,
        filters,
      });
      setIsPopperVisible(false);
    } else {
      setIsPopperVisible((prevState) => !prevState);
    }
  };

  const onCancelModalToggle = () => {
    const isAnyTaskChecked = checkTasksIds.length > 0 || selectAll;
    if (isAnyTaskChecked) {
      openCancelTasksModal({
        checkedTasksId: checkTasksIds,
        onSuccess: invalidateQueries,
        totalTasks,
      });
      setIsPopperVisible(false);
    } else {
      setIsPopperVisible((prevState) => !prevState);
    }
  };

  const handleFiltersChange = (filtersData: FilterData<DrawerFiltersData>) => {
    setFilters({
      ...defaultsDeep(defaultFilters, filtersData),
      ...filtersData,
    });
  };

  const observer = useRef<IntersectionObserver>(null);
  const lastElementRef = useCallback(
    (element: Element) => {
      if (isLoading) {
        return;
      }
      observer.current = new IntersectionObserver(([entry], internalObserver) => {
        if (entry?.isIntersecting && hasNextPage) {
          fetchNextPage();
          internalObserver.disconnect();
        }
      });
      if (element) {
        observer.current.observe(element);
      }
    },
    [fetchNextPage, hasNextPage, isLoading],
  );

  const toggleTreeView = useCallback(() => {
    onCheckAll(false);
    setTreeView(!isTreeViewEnabled);
  }, [onCheckAll, isTreeViewEnabled, setTreeView]);

  const debounceToggleTreeView = useMemo(() => debounce(toggleTreeView, 300), [toggleTreeView]);

  useEffect(() => () => {
    debounceToggleTreeView.cancel();
  });

  const displayState = useMemo(() => {
    if (isLoading) {
      return DISPLAY_STATE.LOADING;
    } else if ((tasksList.length > 0 && !isLoading) || isFetching) {
      return DISPLAY_STATE.SHOW;
    } else if (tasksList.length === 0 && !isLoading && !isFetching) {
      return DISPLAY_STATE.EMPTY;
    }
  }, [isFetching, isLoading, tasksList.length]);

  const hasMoreToLoad = tasksList.length < totalTasks;

  const displayedCount = selectAll ? totalTasks - checkTasksIds.length : checkTasksIds.length;

  return (
    <Drawer>
      <Drawer.Header
        tabs={
          <TasksDrawerFilters
            filters={filters as FilterDataTyped<DrawerFiltersData>}
            isAllTasksChecked={selectAll && checkTasksIds.length > 0}
            isTreeViewEnabled={isTreeViewEnabled}
            toggleTreeView={debounceToggleTreeView}
            onChange={handleFiltersChange}
            onToggleChecked={onCheckAll}
          />
        }
        title={
          <div className="flex items-center gap-2">
            <span className="text-t24-sb">{tWrapper('tasks')} </span>
            <Indicator count={totalToDoTasks} />
          </div>
        }
        onClose={closeProductionPlanTasksDrawer}
      />

      {displayState === DISPLAY_STATE.LOADING &&
        (isTreeViewEnabled ? <TreeViewSkeleton rowsCount={5} /> : <FlatViewSkeleton rowsCount={5} />)}
      {displayState === DISPLAY_STATE.EMPTY && <TasksDrawerEmptyState onRefresh={invalidateQueries} />}
      {displayState === DISPLAY_STATE.SHOW && (
        <>
          {isTreeViewEnabled ? (
            <ToDoListTreeView
              displayField={mainField}
              hasMoreToLoad={hasMoreToLoad}
              isCheckAllFilterChecked={isCheckAllFilterChecked}
              lastElementRef={lastElementRef}
              tasksList={taskViewType === TaskViewType.TREE ? tasksList : []}
              onCheck={handleTaskCheck}
              onMoreClick={handleMoreClick()}
            />
          ) : (
            <ToDoListFlatView
              displayField={mainField}
              hasMoreToLoad={hasMoreToLoad}
              isCheckAllFilterChecked={isCheckAllFilterChecked}
              lastElementRef={lastElementRef}
              tasksList={taskViewType === TaskViewType.FLAT ? tasksList : []}
              onCheck={handleTaskCheck}
              onMoreClick={handleMoreClick()}
            />
          )}

          <Drawer.Footer className="relative">
            {(checkTasksIds.length > 0 || selectAll) && (
              <div className="mr-auto">
                <Indicator count={displayedCount} />
                <span className="pl-1 text-s11">{`/ ${totalTasks}`}</span>
              </div>
            )}

            {isPopperVisible && (
              <div className="absolute -top-7 right-6 rounded-xs bg-icon-primary p-2 text-t12-sb text-text-primary-inverse shadow-small">
                {tWrapper('autoPlanModal.modalButtonEmptyState')}
              </div>
            )}
            {canEditPlan && simulationId !== 0 && (
              <Button variant="outlined" onClick={onCancelModalToggle}>
                {tWrapper('cancelTaskModal.modalButton')}
              </Button>
            )}
            {canUsePlanningAlgorithm && modules.production.autoPlan && simulationId !== 0 && (
              <Button variant="contained" onClick={onAutoplanModalToggle}>
                {tWrapper('autoPlanModal.modalButton')}
              </Button>
            )}
          </Drawer.Footer>
        </>
      )}
    </Drawer>
  );
};

export const { toggleProductionPlanTasksDrawer, closeProductionPlanTasksDrawer } = createDrawer(
  ProductionPlanTasksDrawer,
  {
    name: 'ProductionPlanTasksDrawer',
    slot: 'left',
    width: 420,
    drawerId: () => 'production-plan-tasks-drawer',
  },
);
